import type {AccordionProps, SxProps, Theme} from '@mui/material';
import {Accordion, AccordionDetails, AccordionSummary} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import type {MouseEvent, ReactNode} from 'react';

const TABLE_ACCORDION_CLASS_NAME = 'AccordionLight-root';

const accordionSx: SxProps<Theme> = {
    width: '100%',
    minWidth: 0,
    color: 'inherit',
    font: 'inherit',
    backgroundColor: 'transparent',
    backgroundImage: 'none',
    boxShadow: 'none',
    '&::before': {display: 'none'},
    '&.Mui-expanded': {margin: 0},
};

const summarySx: SxProps<Theme> = {
    minHeight: 0,
    padding: 0,
    justifyContent: 'flex-start',
    gap: 0.5,
    color: 'inherit',
    font: 'inherit',
    '&.Mui-expanded': {minHeight: 0},
    '& .MuiAccordionSummary-content': {
        margin: 0,
        minWidth: 0,
        flexGrow: 0,
        '&.Mui-expanded': {margin: 0},
    },
    '& .MuiAccordionSummary-expandIconWrapper': {
        padding: 0,
        margin: 0,
        flexShrink: 0,
        color: 'inherit',
    },
};

const detailsSx: SxProps<Theme> = {
    padding: 0,
    color: 'inherit',
    font: 'inherit',
};

export type Props = {
    summary: ReactNode;
    children: ReactNode;
    className?: string;
} & Pick<AccordionProps, 'expanded' | 'defaultExpanded' | 'onChange' | 'disabled'>;

function stopRowClick(event: MouseEvent) {
    event.stopPropagation();
}

/** Padding-free MUI Accordion wrapper sized to sit inside a table cell. */
export const AccordionLight = ({
                                   summary,
                                   children,
                                   className,
                                   ...props
                               }: Props) => (
    <Accordion
        {...props}
        disableGutters
        elevation={0}
        square
        className={className ? `${TABLE_ACCORDION_CLASS_NAME} ${className}` : TABLE_ACCORDION_CLASS_NAME}
        sx={accordionSx}
        onClick={stopRowClick}
    >
        <AccordionSummary expandIcon={<ExpandMoreIcon fontSize="small"/>} sx={summarySx}>
            {summary}
        </AccordionSummary>
        <AccordionDetails sx={detailsSx}>
            {children}
        </AccordionDetails>
    </Accordion>
);
